import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import { useStudents } from 'context/StudentsContext';
import { useIEPGoals } from 'context/IEPGoalsContext';
import { useIncidents } from 'context/IncidentsContext';
import { useUserProfiles } from 'context/UserProfilesContext';

// ---------------------------------------------------------------------------
// TeamStatsTiles — the four "at a glance" metrics that quantify how much
// the collaboration is actually saving the team's time:
//
//   * Teammates — the size of the workspace (proof it IS multi-user).
//   * Shared students — students at least one team member has touched.
//   * Data points this week — cross-team IEP goal + incident logging.
//   * Distinct contributors this week — how many people actually logged.
//
// All values come from the same contexts the rest of the app uses, so
// they update in realtime as anyone on the team adds data.
// ---------------------------------------------------------------------------

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function withinLastWeek(iso) {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t < 7 * MS_PER_DAY;
}

const TeamStatsTiles = () => {
  const { profiles } = useUserProfiles();
  const { students } = useStudents();
  const { goals } = useIEPGoals();
  const { incidents } = useIncidents();

  const stats = useMemo(() => {
    const activeStudents = (Array.isArray(students) ? students : []).filter(
      (s) => (s.status || 'active') !== 'archived',
    );

    const goalsThisWeek = (Array.isArray(goals) ? goals : []).filter((g) =>
      withinLastWeek(g.lastUpdated || g.createdAt),
    );
    const incidentsThisWeek = (Array.isArray(incidents) ? incidents : []).filter(
      (i) => withinLastWeek(i.occurredAt || i.createdAt),
    );

    const contributorIds = new Set();
    for (const g of goalsThisWeek) if (g.createdBy) contributorIds.add(g.createdBy);
    for (const i of incidentsThisWeek) if (i.reportedBy) contributorIds.add(i.reportedBy);

    return {
      teammates: (profiles || []).length,
      sharedStudents: activeStudents.length,
      updatesThisWeek: goalsThisWeek.length + incidentsThisWeek.length,
      contributorsThisWeek: contributorIds.size,
    };
  }, [profiles, students, goals, incidents]);

  const tiles = [
    {
      key: 'teammates',
      label: 'Teammates',
      value: stats.teammates,
      caption: 'Everyone signed in to your workspace',
      icon: 'Users',
      tint: 'text-primary bg-primary/10',
    },
    {
      key: 'students',
      label: 'Shared students',
      value: stats.sharedStudents,
      caption: 'Visible to your entire team',
      icon: 'UserSquare',
      tint: 'text-emerald-700 bg-emerald-100',
    },
    {
      key: 'updates',
      label: 'Updates this week',
      value: stats.updatesThisWeek,
      caption: 'Goal edits + behavior incidents',
      icon: 'Activity',
      tint: 'text-blue-700 bg-blue-100',
    },
    {
      key: 'contributors',
      label: 'Contributors',
      value: stats.contributorsThisWeek,
      caption: 'Teammates who logged something',
      icon: 'PenLine',
      tint: 'text-purple-700 bg-purple-100',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {tiles.map((tile) => (
        <div
          key={tile.key}
          className="bg-card border border-border rounded-xl p-4 flex items-start gap-3"
        >
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center ${tile.tint}`}
            aria-hidden="true"
          >
            <Icon name={tile.icon} size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">
              {tile.label}
            </p>
            <p className="text-2xl font-bold text-card-foreground leading-tight">
              {tile.value}
            </p>
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {tile.caption}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default TeamStatsTiles;
